import React, { useState, useCallback, useEffect } from 'react'

import cn from 'classnames'
import styles from './styles.module.scss'

import Input from '../../../UI/Input/Input'
import Row from '../../../UI/Table/Row'
import Table from '../../../UI/Table/Table'
import TableField from '../../../UI/Table/TableField'
import TableHeadField from '../../../UI/Table/TableHeadField'
import DateRangePopup from '../../RangeDatePicker/DateRangePopup'

import { ReactComponent as CalendarIcon } from './icons/calendar.svg'
import { ReactComponent as LoginIcon } from './icons/login.svg'
import { ReactComponent as LogoutIcon } from './icons/logout.svg'

import ReactDOM from 'react-dom'
import { getLoginHistoryList } from '../../../api/getApi'
import { objToQueryConverter } from '../../../helpers/apiHelper'
import { getDateByFormat } from '../../../helpers/time-helper/time-helper'
import useClickOutside from '../../../hooks/useClickOutside'
import useInfiniteScroll from '../../../hooks/useInfiniteScroll'
import { getDefaultParams } from '../Charts/utils'
import { tHead } from './config'
import { debounce } from 'lodash'

const emptyMessage = <div className={styles.noContent}>No results found</div>

const LoginHistoryTable = () => {
  const [list, setList] = useState([])
  const [isOpenDatePicker, setIsOpenDatePicker] = useState(false)
  const [viewHistorySession, setViewHistorySession] = useState(null)
  const [isHistoryHover, setIsHistoryHover] = useState(false)
  const [params, setParams] = useState(getDefaultParams)
  const [searchInput, setSearchInput] = useState('')

  const ref = useClickOutside(() => setViewHistorySession(null))

  const { handleScroll, loading } = useInfiniteScroll(
    getLoginHistoryList,
    setList,
    null,
    objToQueryConverter(params),
    null,
    true
  )

  const renderHead = () => (
    <Row isHead isSortLabel>
      {tHead.map((item) => (
        <TableHeadField key={item}>{item}</TableHeadField>
      ))}
    </Row>
  )

  const renderHistorySession = (items) => {
    return items.map(({ created_at, closed_at }, index) => (
      <div className={styles.historyItem} key={`${created_at}-${closed_at}-${index}`}>
        <div className={styles.time}>
          <span>
            <LoginIcon />
          </span>
          <span>{getDateByFormat(created_at, 'HH:mm')}</span>
        </div>

        <div className={styles.time}>
          <span>
            <LogoutIcon />
          </span>
          <span>{getDateByFormat(closed_at, 'HH:mm')}</span>
        </div>
      </div>
    ))
  }

  const renderBody = () => {
    return list.map(({ min_date, max_date, full_name, position, work_time, date, items }, index) => {
      const minDate = getDateByFormat(min_date, 'hh:mm aa')
      const maxDate = getDateByFormat(max_date, 'hh:mm aa')
      const userLoginDate = getDateByFormat(date, 'dd.MM.yy')
      const workTime = `${work_time.hours}h ${work_time.minutes}m`

      return (
        <Row key={index}>
          <TableField>{full_name || 'No information'}</TableField>
          <TableField>{position.name || 'No information'}</TableField>
          <TableField>{minDate}</TableField>
          <TableField>{maxDate}</TableField>
          <TableField>
            <div className={styles.totalTime}>
              <span>{userLoginDate}</span>

              <span
                className={cn(styles.workTime, { [styles.workTime__underline]: viewHistorySession?.id === index })}
                onClick={(e) => handleHistoryView(index, e)}
              >
                {workTime}
              </span>

              {viewHistorySession?.id === index &&
                ReactDOM.createPortal(
                  <div
                    className={styles.historyContainer}
                    onMouseEnter={() => setIsHistoryHover(true)}
                    onMouseLeave={() => setIsHistoryHover(false)}
                    onWheelCapture={(e) => {
                      const el = e.currentTarget
                      el.scrollTop += e.deltaY
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onTouchMoveCapture={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    style={{
                      position: 'absolute',
                      top: `${viewHistorySession.top}px`,
                      left: `${viewHistorySession.left}px`,
                      zIndex: 9999,
                    }}
                    ref={ref}
                  >
                    {renderHistorySession(items)}
                  </div>,
                  document.body
                )}
            </div>
          </TableField>
        </Row>
      )
    })
  }

  const handleButtonClick = () => {
    setIsOpenDatePicker(!isOpenDatePicker)
  }

  const handleHistoryView = (id, event) => {
    const rect = event.target.getBoundingClientRect()
    setViewHistorySession({
      id,
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    })
  }

  const debouncedSetParams = useCallback(
    debounce((value) => {
      setParams((prevParams) => ({ ...prevParams, search: value }))
    }, 300),
    []
  )

  useEffect(() => {
    debouncedSetParams(searchInput)
  }, [searchInput, debouncedSetParams])

  const handleInputChange = (e) => {
    const { value } = e.target
    setSearchInput(value)
  }

  const handleRangeChange = (_, dates) => {
    const start_date = getDateByFormat(dates.from, 'yyyy-MM-dd')
    const end_date = getDateByFormat(dates.to, 'yyyy-MM-dd')

    setParams({ ...params, start_date, end_date })
  }

  const isDateEqual = getDefaultParams().start_date === params.start_date
  const range = isDateEqual
    ? null
    : {
        from: new Date(params.start_date),
        to: new Date(params.end_date),
      }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h5 className={styles.title}>User login history table</h5>

        <div className={styles.filter}>
          <Input
            name="search"
            placeholder="First name, second name"
            className={styles.input}
            value={searchInput}
            onChange={handleInputChange}
            loupe
          />

          <div className={styles.groupWrapper}>
            <button
              className={cn(
                styles.button,
                { [styles.activeButton]: isOpenDatePicker },
                { [styles.activeWithValue]: !isDateEqual }
              )}
              onClick={handleButtonClick}
            >
              <CalendarIcon />
            </button>

            {isOpenDatePicker && (
              <DateRangePopup
                onChange={handleRangeChange}
                range={range}
                maxDate={new Date()}
                onClose={() => setIsOpenDatePicker(false)}
              />
            )}
          </div>
        </div>
      </div>

      <Table
        head={renderHead()}
        body={renderBody()}
        onScroll={() => {
          // Do not close popup while user is interacting with it.
          if (!isHistoryHover) setViewHistorySession(null)
        }}
        loading={loading}
        colSpan={5}
        emptyMessage={emptyMessage}
      />
    </div>
  )
}

export default LoginHistoryTable
